import { isWidgetPending } from '@cascade/react/experiences';
import { useI18n } from '@cascade/react/i18n';
import {
  cardVariants,
  Collapsible,
  Divider,
  Loading,
  ToolButton,
} from '@cascade/ui-library';
import clsx from 'clsx';
import { useId, useState } from 'react';
import type { CSSProperties } from 'react';
import classes from './ExperienceSection.module.scss';
import type { TExperienceSectionProps } from './ExperienceSection.types.ts';
import { ExperienceWidget } from './experience-widget/index.ts';
import { HelpButton } from './help-button/index.ts';
import { SideStepButton } from './side-step-button/index.ts';
import { ExperienceHeading } from '#views/experience/experience-step/experience-heading/index.ts';

export const ExperienceSection = ({
  section,
  failureMessage,
  isLast,
}: TExperienceSectionProps) => {
  const isInitiallyCollapsed = Boolean(section.default_collapsed);
  const semanticColor = cardVariants.find(
    (variant) => variant === section.semantic_color,
  );

  const isPlain = !semanticColor || semanticColor === 'default';
  const showDivider = isPlain && !isLast;
  const isPending = section.widgets.some(isWidgetPending);
  const showLoader = isPending && !failureMessage;
  const gridStyle = { '--divisions': section.divisions } as CSSProperties;

  const [isCollapsed, setIsCollapsed] = useState(isInitiallyCollapsed);
  const bodyId = useId();
  const { translate } = useI18n();

  const toggleLabel = isCollapsed
    ? translate('experience.section.expand')
    : translate('experience.section.collapse');

  const toggleCollapsed = () => setIsCollapsed((collapsed) => !collapsed);

  const body = (
    <div id={bodyId}>
      {showLoader ? (
        <Loading contain label={translate('experience.content.generating')} />
      ) : (
        <div className={classes.grid} style={gridStyle}>
          {section.widgets.map((widget) => {
            const cellStyle = { '--span': widget.span } as CSSProperties;

            return (
              <div key={widget.id} className={classes.cell} style={cellStyle}>
                <ExperienceWidget
                  widget={widget}
                  failureMessage={failureMessage}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <>
      <section
        className={clsx(
          classes['experience-section'],
          semanticColor ? classes[semanticColor] : null,
        )}
      >
        <div className={classes.header}>
          <ExperienceHeading
            icon={section.icon}
            iconColor={section.icon_color}
            title={section.title}
            description={section.description}
            level='h3'
          />
          {section.side_step_button ? (
            <SideStepButton button={section.side_step_button} />
          ) : null}
        </div>
        {section.collapsible ? (
          <>
            <Collapsible
              label={section.title}
              collapsed={isCollapsed}
              direction='vertical'
            >
              {body}
            </Collapsible>
            <div className={classes.toggle}>
              <ToolButton
                iconType={isCollapsed ? 'chevron-down' : 'chevron-up'}
                label={toggleLabel}
                aria-expanded={!isCollapsed}
                aria-controls={bodyId}
                onClick={toggleCollapsed}
              />
            </div>
          </>
        ) : (
          body
        )}
        {section.help_prompt ? (
          <HelpButton prompt={section.help_prompt} />
        ) : null}
      </section>
      {showDivider ? <Divider /> : null}
    </>
  );
};
